import { useCallback, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import type { SplaConsumptionRow } from '../types/models/Spla';
import type { ApiResponseSplaConsumptionList } from '../types/api/ApiResponseSpla';

interface UseAdminSplaConsumptionReturn {
  consumption: SplaConsumptionRow[];
  loading: boolean;
  /** Human-readable error message (the backend's JSend `message`), or null. */
  error: string | null;
  refetch: () => Promise<void>;
  /** Replace one row in place (matched by `id`) from a PATCH response — no full refetch. */
  applyRow: (updated: SplaConsumptionRow) => void;
  /** Drop one row after a DELETE. */
  removeRow: (id: number) => void;
}

// GET /admin/spla/consumption[?client_id=N] — the CURRENT per-subject consumption state, ordered by
// client, license, subject. Staff-only; `enabled` defers the fetch until the page's staff check
// passes (the useAdminProducts precedent). `refreshKey` bumps force a refetch — the Bindings tab
// invalidates this list after a recompute / binding create / binding delete, all of which maintain
// `computed` rows here. Branch on the JSend `body.status`, NEVER `response.ok`.
export const useAdminSplaConsumption = (
  enabled: boolean,
  clientId: number | null,
  refreshKey = 0,
): UseAdminSplaConsumptionReturn => {
  const [consumption, setConsumption] = useState<SplaConsumptionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const auth = useAuth();
  const apiFetch = useApiFetch();

  const fetchConsumption = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const query = clientId !== null ? `?client_id=${clientId}` : '';
      const response = await apiFetch(`/admin/spla/consumption${query}`);
      const body = (await response.json().catch(() => null)) as ApiResponseSplaConsumptionList | null;
      if (body?.status === 'success' && body.data?.consumption) {
        setConsumption(body.data.consumption);
      } else {
        setError(body?.message ?? 'Failed to load the SPLA consumption');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while fetching the SPLA consumption');
    } finally {
      setLoading(false);
    }
  }, [apiFetch, clientId]);

  const applyRow = useCallback((updated: SplaConsumptionRow) => {
    setConsumption((prev) => prev.map((row) => (row.id === updated.id ? updated : row)));
  }, []);

  const removeRow = useCallback((id: number) => {
    setConsumption((prev) => prev.filter((row) => row.id !== id));
  }, []);

  useEffect(() => {
    if (enabled && auth.isAuthenticated) {
      void fetchConsumption();
    }
    // refreshKey re-runs the fetch when the Bindings tab changes computed rows.
  }, [enabled, auth.isAuthenticated, fetchConsumption, refreshKey]);

  return { consumption, loading, error, refetch: fetchConsumption, applyRow, removeRow };
};
